import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// The sync actions an operator can start. Every one is a POST with no body: the values below become QUERY parameters.
// `scope` says what it touches: "Global" (everything), "Org", "User", or a mix.
//
// Field kinds:
//   org       { kind: "org", required }                      -> org_id
//   text      { kind: "text", param, label, required }
//   number    { kind: "number", param, label, def, min, max } -> sent only when changed from the default
//   dates     { kind: "dates", whenEmpty }                    -> start_date / end_date (YYYY-MM-DD, inclusive)
//   date      { kind: "date", param, label, help }            -> one optional YYYY-MM-DD parameter (e.g. target_date)
//   platform  { kind: "platform" }                            -> platform_id (empty = all enabled platforms)
//   bypass    { kind: "bypass" }                              -> bypass_dedup=true
//   force     { kind: "force" }                               -> force=true
export const SYNC_ACTIONS = [
  {
    id: "user-by-org",
    group: "Users & organizations",
    title: "Sync users for an organization",
    description: "Refreshes one organization's members from GitHub.",
    scope: "Org",
    task: "sync.user.by_org",
    queue: "sync.user",
    path: "user/by-org",
    fields: [{ kind: "org", required: true }],
  },
  {
    id: "verify-pending-all",
    group: "Users & organizations",
    title: "Verify pending memberships (all orgs)",
    description:
      "Joins REQUESTED users who are already in GitHub and removes REMOVE_REQUESTED users who are gone. This changes data. For one organization use the panel on the Overview tab.",
    scope: "Global",
    task: "sync.user.verify_pending",
    queue: "sync.user",
    path: "user/verify-pending",
    fields: [],
    confirm: true,
  },
  {
    id: "org-owners",
    group: "Users & organizations",
    title: "Sync organization owners",
    description: "Updates owners from GitHub. Pick an organization, or leave it empty for every active organization.",
    scope: "Org / Global",
    task: "sync.org.owners",
    queue: "sync.org",
    path: "org/owners",
    fields: [{ kind: "org", required: false }],
  },
  {
    id: "remove-inactive",
    group: "Users & organizations",
    title: "Process inactive-user removals",
    description:
      "Requests removal for inactive users whose notice period has ended (REMOVE_REQUESTED). It is not an instant GitHub delete.",
    scope: "Global",
    task: "sync.user.remove_inactive",
    queue: "sync.user",
    path: "user/remove-inactive",
    fields: [],
    confirm: true,
    danger: true,
  },
  {
    id: "user-cost-center",
    group: "Users & organizations",
    title: "Assign missing user cost centers",
    description:
      "Finds GitHub users with no cost center, works it out from country and OpData, then updates the profile and the GitHub team.",
    scope: "Global",
    task: "sync.user.cost_center",
    queue: "sync.user",
    path: "user/cost-center",
    fields: [],
    confirm: true,
  },
  {
    id: "set-end-date",
    group: "Users & organizations",
    title: "Backfill removed users' end dates",
    description: "Sets the end date where every organization is Removed and none was recorded. No input needed.",
    scope: "Global",
    task: "sync.user.set_end_date",
    queue: "sync.user",
    path: "user/set-end-date",
    fields: [],
    confirm: true,
  },
  {
    id: "cost-center-import",
    group: "Cost centers",
    title: "Import cost centers from GitHub",
    description: "Pulls the enterprise billing cost centers into Arbiter (creates and updates).",
    scope: "Global",
    task: "sync.cost_center.from_github",
    queue: "sync.cost_center",
    path: "cost-center/sync-from-github",
    fields: [],
    confirm: true,
  },
  {
    id: "copilot-org-usage",
    group: "Copilot",
    title: "Populate organization Copilot usage",
    description: "Loads Copilot usage per organization for a date range. With no dates it does today (UTC).",
    scope: "Global",
    task: "sync.copilot.usage.org.populate",
    queue: "sync.copilot",
    path: "copilot/org/usage",
    fields: [{ kind: "dates", whenEmpty: "today (UTC)" }, { kind: "bypass" }],
  },
  {
    id: "copilot-user-report",
    group: "Copilot",
    title: "Ingest user Copilot usage report",
    description:
      "Requests the report, waits for it, then loads each user's usage. It can run for a long time, so follow it in the Task tracker.",
    scope: "Global",
    task: "sync.copilot.usage.user.report",
    queue: "sync.copilot",
    path: "copilot/usage/user-report",
    fields: [{ kind: "dates", whenEmpty: "today (UTC)" }, { kind: "bypass" }],
  },
  {
    id: "copilot-budget-dispatch",
    group: "Copilot",
    title: "Sync Copilot budgets (bulk)",
    description:
      "Queues budget syncs for eligible users in batches. When it finishes it has only handed the batches out, not finished every user.",
    scope: "Org / Global",
    task: "sync.copilot.budget.users.dispatch",
    queue: "sync.copilot",
    path: "copilot/budget/users/dispatch",
    fields: [
      { kind: "org", required: false },
      { kind: "number", param: "max_users_per_hour", label: "Candidates per run", def: 2800, min: 1, max: 50000 },
      { kind: "number", param: "users_per_task", label: "Users per task", def: 50, min: 1, max: 2000 },
      { kind: "number", param: "min_hours_since_update", label: "Skip users updated within (hours)", def: 24, min: 1, max: 720 },
      { kind: "date", param: "target_date", label: "Candidate scan date (optional)", help: "Leave empty for the usual scan." },
      { kind: "bypass" },
    ],
  },
  {
    id: "copilot-budget-user",
    group: "Copilot",
    title: "Sync one user's Copilot budget",
    description: "Refreshes a single person's budget from GitHub.",
    scope: "User",
    task: "sync.copilot.budget.user",
    queue: "sync.copilot",
    path: "copilot/budget/user",
    fields: [
      { kind: "org", required: true },
      { kind: "text", param: "user_id", label: "Arbiter user ID", required: true },
      { kind: "text", param: "github_login", label: "GitHub login", required: true, help: "The login name, not the numeric ID." },
      { kind: "number", param: "min_hours_since_update", label: "Skip if updated within (hours)", def: 24, min: 1, max: 720 },
      { kind: "force" },
    ],
  },
  {
    id: "metered-usage",
    group: "Metered usage",
    title: "Ingest metered billing usage",
    description:
      "Loads one detailed billing report per day. With no dates it does yesterday (UTC). Long ranges take a long time.",
    scope: "Global / Platform",
    task: "sync.metered.usage.report",
    queue: "sync.metered",
    path: "metered/usage",
    fields: [{ kind: "dates", whenEmpty: "yesterday (UTC)" }, { kind: "platform" }, { kind: "bypass" }],
  },
];

// The order the groups are shown in: the most-used first. A group not listed here still shows, after these.
const GROUP_ORDER = ["Copilot", "Metered usage", "Users & organizations", "Cost centers"];
export const ACTION_GROUPS = [
  ...GROUP_ORDER.filter((group) => SYNC_ACTIONS.some((action) => action.group === group)),
  ...new Set(SYNC_ACTIONS.map((action) => action.group).filter((group) => !GROUP_ORDER.includes(group))),
];

export const getAction = (id) => SYNC_ACTIONS.find((action) => action.id === id);

const hasKind = (action, kind) => action.fields.some((field) => field.kind === kind);

export const emptyValues = (action) => {
  const values = { queue: "" };
  action.fields.forEach((field) => {
    if (field.kind === "org") values.org = "";
    if (field.kind === "text") values[field.param] = "";
    if (field.kind === "number") values[field.param] = String(field.def);
    if (field.kind === "date") values[field.param] = "";
    if (field.kind === "dates") {
      values.start_date = "";
      values.end_date = "";
    }
    if (field.kind === "platform") values.platform_id = "";
    if (field.kind === "bypass") values.bypass_dedup = false;
    if (field.kind === "force") values.force = false;
  });
  return values;
};

const validDate = (text) => dayjs(text, "YYYY-MM-DD", true).isValid();

// Returns { fieldKey: "message" } — empty when everything is fine. Keys: org, <param>, start_date, end_date.
export const validateValues = (action, values) => {
  const errors = {};
  action.fields.forEach((field) => {
    if (field.kind === "org" && field.required && !String(values.org || "").trim()) errors.org = "Pick an organization.";
    if (field.kind === "text" && field.required && !String(values[field.param] || "").trim()) errors[field.param] = "This is required.";
    if (field.kind === "number") {
      const raw = String(values[field.param] ?? "").trim();
      const number = Number(raw);
      if (raw === "" || !Number.isInteger(number) || number < field.min || number > field.max) {
        errors[field.param] = `Enter a whole number from ${field.min.toLocaleString("en-US")} to ${field.max.toLocaleString("en-US")}.`;
      }
    }
  });
  action.fields.forEach((field) => {
    if (field.kind === "date" && String(values[field.param] || "").trim() && !validDate(String(values[field.param]).trim())) errors[field.param] = "Use a valid date.";
  });
  if (hasKind(action, "dates")) {
    const start = String(values.start_date || "").trim();
    const end = String(values.end_date || "").trim();
    // With only an end date the API would default the start to today, not to the end date, so a start is required.
    if (end && !start) errors.start_date = "Enter a start date when there is an end date.";
    if (start && !validDate(start)) errors.start_date = "Use a valid date.";
    if (end && !validDate(end)) errors.end_date = "Use a valid date.";
    if (start && end && !errors.start_date && !errors.end_date && dayjs(end).isBefore(dayjs(start), "day")) {
      errors.end_date = "The end date is before the start date.";
    }
  }
  return errors;
};

// The query parameters to send: only what is set, and numbers only when changed from the API's own default.
export const buildParams = (action, values) => {
  const params = {};
  action.fields.forEach((field) => {
    if (field.kind === "org" && String(values.org || "").trim()) params.org_id = String(values.org).trim();
    if (field.kind === "text") params[field.param] = String(values[field.param] || "").trim();
    if (field.kind === "number" && Number(values[field.param]) !== field.def) params[field.param] = Number(values[field.param]);
    if (field.kind === "date" && String(values[field.param] || "").trim()) params[field.param] = String(values[field.param]).trim();
    if (field.kind === "dates") {
      if (String(values.start_date || "").trim()) params.start_date = values.start_date.trim();
      if (String(values.end_date || "").trim()) params.end_date = values.end_date.trim();
    }
    if (field.kind === "platform" && String(values.platform_id || "").trim()) params.platform_id = values.platform_id.trim();
    if (field.kind === "bypass" && values.bypass_dedup) params.bypass_dedup = true;
    if (field.kind === "force" && values.force) params.force = true;
  });
  if (String(values.queue || "").trim()) params.queue = String(values.queue).trim();
  return params;
};

// Global actions, removals, a bypassed duplicate guard, forced updates, and an optional organization left empty
// (which means every organization) all need an explicit confirmation.
export const needsConfirmation = (action, values) =>
  Boolean(
    action.confirm ||
      action.danger ||
      values.bypass_dedup ||
      values.force ||
      (action.fields.some((field) => field.kind === "org" && !field.required) && !String(values.org || "").trim())
  );

// The date range as shown in a confirmation or the tracker.
export const describeDates = (action, values) => {
  const field = action.fields.find((f) => f.kind === "dates");
  if (!field) return null;
  const start = String(values.start_date || "").trim();
  const end = String(values.end_date || "").trim();
  if (!start) return `Default: ${field.whenEmpty}`;
  return end && end !== start ? `${start} → ${end}` : start;
};

export const describeScope = (action, values, orgName) => {
  const hasOrg = action.fields.some((field) => field.kind === "org");
  if (hasOrg) return String(values.org || "").trim() ? `Organization · ${orgName || values.org}` : "All active organizations";
  return action.scope.startsWith("Global") ? "Everything (global)" : action.scope;
};
